import { useEffect, useState } from 'react';
import {
  SERVICE_CATEGORY_LABELS,
  type ServiceDashboard as Board, type ServiceRegionScopeView,
} from 'shared';
import { api, ApiError } from '../lib/api';
import { Icon } from '../components/icons';

type Period = 'month' | 'week' | 'today';

const PERIOD_LABELS: Record<Period, string> = {
  today: 'Hari ini',
  week: '7 hari terakhir',
  month: 'Bulan ini',
};

/** Jakarta has no DST, so the offset is always +07:00 (cross-phase rule 9). */
function jakartaParts(d: Date) {
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => p.find((x) => x.type === t)?.value ?? '01';
  return { y: get('year'), m: get('month'), d: get('day') };
}

function periodStart(period: Period): string {
  const now = new Date();
  const { y, m, d } = jakartaParts(now);
  if (period === 'month') return `${y}-${m}-01T00:00:00+07:00`;
  if (period === 'today') return `${y}-${m}-${d}T00:00:00+07:00`;
  const week = new Date(now.getTime() - 6 * 86400000);
  const w = jakartaParts(week);
  return `${w.y}-${w.m}-${w.d}T00:00:00+07:00`;
}

/** Minutes read as minutes up to an hour, then as hours, then as days. */
function duration(minutes: number | null): string {
  if (minutes === null) return '—';
  if (minutes < 60) return `${minutes} mnt`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} jam`;
  return `${Math.round(hours / 24)} hari`;
}

/**
 * Dasbor Layanan — item 9 of the brief.
 *
 * Every figure comes from `service_request` and its status history. No counter columns
 * anywhere, following the rule that aggregates are always derived: a stored count drifts
 * the first time a request is cancelled or a status corrected, and it drifts silently.
 */
export function ServiceDashboard() {
  const [period, setPeriod] = useState<Period>('month');
  const [regionId, setRegionId] = useState('');
  const [scope, setScope] = useState<ServiceRegionScopeView | null>(null);
  const [board, setBoard] = useState<Board | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api.get<ServiceRegionScopeView>('/services/regions/mine')
      .then((r) => { if (!cancelled) setScope(r); })
      .catch(() => { /* the dashboard still works unfiltered */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ from: periodStart(period) });
    if (regionId) params.set('regionId', regionId);

    api.get<Board>(`/services/dashboard?${params}`)
      .then((b) => { if (!cancelled) { setBoard(b); setError(null); } })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : 'Gagal memuat dasbor.');
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [period, regionId]);

  const regionMax = Math.max(1, ...(board?.byRegion.map((r) => r.total) ?? [1]));
  const typeMax = Math.max(1, ...(board?.byType.map((t) => t.total) ?? [1]));

  return (
    <div className="stack">
      <div className="page-head">
        <h2>Dasbor Layanan</h2>
        <p>
          {PERIOD_LABELS[period]}
          {scope?.scoped
            ? ` · ${scope.regions[0]?.name ?? 'wilayah belum diatur'}`
            : regionId
              ? ` · ${scope?.regions.find((r) => r.id === regionId)?.name ?? ''}`
              : ' · seluruh wilayah'}
        </p>
      </div>

      <div className="filter-row">
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value as Period)}
          aria-label="Periode"
        >
          {(Object.keys(PERIOD_LABELS) as Period[]).map((p) => (
            <option key={p} value={p}>{PERIOD_LABELS[p]}</option>
          ))}
        </select>
        {scope?.scoped ? (
          <span className="scope-chip" title="Akun Anda hanya memegang wilayah ini">
            <Icon name="lock" size={14} />
            <span>Wilayah</span>
            {scope.regions[0]?.name ?? 'belum diatur'}
          </span>
        ) : (
          <select
            value={regionId}
            onChange={(e) => setRegionId(e.target.value)}
            aria-label="Wilayah"
          >
            <option value="">Semua wilayah</option>
            {scope?.regions.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        )}
      </div>

      {error && <div className="msg error">{error}</div>}

      {board && (
        <>
          <div className="stat-row">
            <div className="stat">
              <span className="label">Permintaan masuk</span>
              <strong>{board.totalRequests}</strong>
              <span className="stat-note">{PERIOD_LABELS[period].toLowerCase()}</span>
            </div>

            {/*
              The only tile with the alert treatment, because it is the only figure
              somebody has to act on today. Giving every tile a colour would flatten the
              one that matters into the wallpaper.
            */}
            <div className={board.openRequests > 0 ? 'stat is-alert' : 'stat'}>
              <span className="label">Belum selesai</span>
              <strong>{board.openRequests}</strong>
              <span className="stat-note">
                {board.oldestUnhandledMinutes === null
                  ? 'tidak ada yang menunggu'
                  : `terlama menunggu ${duration(board.oldestUnhandledMinutes)}`}
              </span>
            </div>

            <div className="stat">
              <span className="label">Rata-rata respons</span>
              <strong>{duration(board.meanResponseMinutes)}</strong>
              <span className="stat-note">ambulans dan bencana saja</span>
            </div>

            <div className="stat">
              <span className="label">Selesai</span>
              <strong>{board.completedRequests}</strong>
              <span className="stat-note">
                {board.rejectedRequests} ditolak · {board.cancelledRequests} dibatalkan
              </span>
            </div>
          </div>

          {/* A pinned account has one region: a bar chart of six where five are zero is
              noise, so its per-type breakdown takes the full width instead. */}
          <div className={scope?.scoped ? 'stack' : 'grid-2'}>
            {!scope?.scoped && (
            <section className="card" style={{ padding: '1rem 1.1rem' }}>
              <span className="label">Permintaan per wilayah</span>
              <div className="bar-list">
                {board.byRegion.map((r) => (
                  <div className="bar-row" key={r.regionId}>
                    <span className="bar-name">{r.regionName}</span>
                    <span className="bar-track">
                      <span
                        className="bar-fill"
                        style={{ width: `${Math.round((r.total / regionMax) * 100)}%` }}
                      />
                    </span>
                    <span className="bar-value mono">{r.total}</span>
                  </div>
                ))}
              </div>
              <p className="muted" style={{ fontSize: 11.5, marginTop: '0.7rem' }}>
                Wilayah tanpa permintaan tetap tampil sebagai nol, supaya bedanya dengan
                &quot;belum ada data&quot; jelas.
              </p>
            </section>
            )}

            <section className="card" style={{ padding: '1rem 1.1rem' }}>
              <span className="label">Per jenis layanan</span>
              <div className="bar-list">
                {board.byType.map((t) => (
                  <div className="bar-row" key={t.serviceTypeId}>
                    <span className="bar-name" title={SERVICE_CATEGORY_LABELS[t.category]}>
                      {t.name}
                    </span>
                    <span className="bar-track">
                      <span
                        className="bar-fill is-alt"
                        style={{ width: `${Math.round((t.total / typeMax) * 100)}%` }}
                      />
                    </span>
                    <span className="bar-value mono">{t.total}</span>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <section className="card" style={{ padding: '1rem 1.1rem' }}>
            <span className="label">Sebaran status</span>
            <div className="row" style={{ marginTop: '0.7rem' }}>
              {board.byStatus.length === 0
                ? <p className="muted">Belum ada permintaan pada periode ini.</p>
                : board.byStatus.map((s) => (
                  <span className="pill" key={s.status}>
                    {s.label} · {s.total}
                  </span>
                ))}
            </div>
          </section>
        </>
      )}

      {loading && !board && <p className="muted">Memuat…</p>}
    </div>
  );
}
